import React, { useCallback, useContext } from 'react';

import Tooltip from 'components/Tooltip';
import FleetOpsStaffOnly from 'components/Common/FleetOpsStaffOnly';
import Button from 'kingpin/atoms/Button';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';

import buildInsightsChatLog from './buildInsightsChatLog';

// Two digits, so a filename timestamp sorts and reads cleanly.
const pad = (value: number): string => String(value).padStart(2, '0');

// insights-chat-2026-08-28-1432.md — a local-time stamp so a staffer's
// downloads sort by when THEY exported them.
const buildFileName = (now: Date): string =>
  `insights-chat-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate(),
  )}-${pad(now.getHours())}${pad(now.getMinutes())}.md`;

// Staff-only "download chat log" action for the Insights chat header. Serialises
// the CURRENT conversation (live in-memory messages, so the reasoning trace is
// uncapped) to a Markdown transcript and triggers a browser download. Renders
// nothing for non-staff (the reasoning it exports is staff-only anyway) or when
// there's no conversation to export.
const DownloadChatLogButton = () => {
  const { messages } = useContext(InsightsChatContext);

  const onDownload = useCallback(() => {
    const now = new Date();
    // The provider doesn't expose the active conversation's id/meta (and a
    // fresh, unsaved chat isn't in the history list yet), so build from the live
    // messages — the title derives from the first question.
    const content = buildInsightsChatLog({ messages, exportedAt: now });

    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.setAttribute('download', buildFileName(now));
    anchor.click();
    URL.revokeObjectURL(url);
  }, [messages]);

  if (messages.length === 0) {
    return null;
  }

  return (
    <FleetOpsStaffOnly>
      <Tooltip content="Download log (staff)">
        <Button
          type="Ghost"
          size="Small"
          icon="export"
          onClick={onDownload}
          testId="insights-download-log"
        />
      </Tooltip>
    </FleetOpsStaffOnly>
  );
};

export default DownloadChatLogButton;
